/**
 * PARMAX - Stender
 * Web Component <parmax-stender> (sections/parmax-stender.liquid).
 *
 * - Desktop (hover): il capo sotto il mouse si gira di fronte e si allarga;
 *   a riposo il primo capo resta girato, per suggerire l'interazione.
 * - Touch / mobile: la stella scorre trascinando, il capo al centro si gira;
 *   tap sul capo centrale = apre il dettaglio, tap su un capo laterale = lo porta al centro.
 * - Frecce opzionali (mobile / desktop): girano il capo precedente o successivo.
 * - Dettaglio (overlay): contatore, precedente/successivo, fronte/retro,
 *   modalità "link" oppure "atc" (taglie + aggiunta al carrello col cart drawer del tema).
 */
(() => {
  if (customElements.get('parmax-stender')) return;

  const TRACK_MQ = '(max-width: 767px), (hover: none)';
  const pad = (n) => String(n).padStart(2, '0');

  class ParmaxStender extends HTMLElement {
    constructor() {
      super();
      this.init = this.init.bind(this);
      this.ready = false;
    }

    connectedCallback() {
      if (this.ready) return;
      if (typeof initLazyScript === 'function') {
        initLazyScript(this, this.init);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      if (this.overlay && this.overlay.parentNode && this.overlay.parentNode !== this) {
        this.overlay.remove();
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }
      this.ready = false;
    }

    init() {
      if (this.ready || !this.isConnected) return;
      this.list = this.querySelector('.pxs__list');
      this.stage = this.querySelector('.pxs__stage');
      this.items = Array.from(this.querySelectorAll('.pxs__item'));
      if (!this.list || !this.items.length) return;
      this.ready = true;

      this.nameText = this.querySelector('.pxs__name-text');
      this.priceEl = this.querySelector('.pxs__price');
      this.compareEl = this.querySelector('.pxs__compare');
      this.hintEl = this.querySelector('.pxs__hint');
      this.defaultName = this.nameText ? this.nameText.textContent : '';
      this.overlay = this.querySelector('.pxs-ov');
      this.active = -1;
      this.track = false;
      this.shift = 0;
      this.dragged = false;

      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };

      this.arrows = Array.from(this.querySelectorAll('.js-pxs-step'));
      this.arrows.forEach((btn) => {
        btn.addEventListener('click', () => this.step(Number(btn.dataset.dir)), opts);
      });
      const arrowsWrap = this.querySelector('.pxs__arrows');
      if (arrowsWrap && this.items.length < 2) arrowsWrap.hidden = true;

      this.items.forEach((item, i) => {
        item.addEventListener('pointerenter', (evt) => {
          if (!this.track && evt.pointerType === 'mouse') this.setActive(i);
        }, opts);
        const hit = item.querySelector('.pxs__hit');
        if (!hit) return;
        hit.addEventListener('focus', () => {
          this.setActive(i);
          if (this.track) this.center();
        }, opts);
        hit.addEventListener('click', () => this.onItemClick(i, hit), opts);
      });

      this.list.addEventListener('pointerleave', (evt) => {
        if (!this.track && evt.pointerType === 'mouse' && !this.list.contains(document.activeElement)) {
          this.rest();
        }
      }, opts);
      this.list.addEventListener('focusout', (evt) => {
        if (!this.track && !this.list.contains(evt.relatedTarget)) this.rest();
      }, opts);

      // Trascinamento della stella (modalità touch)
      this.list.addEventListener('pointerdown', (evt) => this.onDragStart(evt), opts);
      this.list.addEventListener('pointermove', (evt) => this.onDragMove(evt), opts);
      this.list.addEventListener('pointerup', (evt) => this.onDragEnd(evt), opts);
      this.list.addEventListener('pointercancel', () => this.onDragCancel(), opts);

      // Theme Editor: selezionando un blocco il capo si gira
      this.addEventListener('shopify:block:select', (evt) => {
        const i = this.items.indexOf(evt.target);
        if (i < 0) return;
        this.setActive(i);
        if (this.track) this.center();
      }, opts);
      this.addEventListener('shopify:block:deselect', () => {
        if (!this.track) this.rest();
      }, opts);

      this.mq = window.matchMedia(TRACK_MQ);
      this.mq.addEventListener('change', () => this.setMode(), opts);
      window.addEventListener('resize', () => {
        if (this.track) this.center(false);
      }, opts);

      if (this.overlay) this.initOverlay(opts);
      this.setMode();
    }

    /* ---------- stella ---------- */

    setMode() {
      this.track = this.mq.matches;
      this.classList.toggle('is-track', this.track);
      if (this.hintEl) {
        const hint = this.track ? this.dataset.hintTouch : this.dataset.hint;
        if (hint) this.hintEl.textContent = hint;
      }
      if (this.track) {
        this.setActive(this.active >= 0 ? this.active : 0);
        this.center(false);
      } else {
        this.shift = 0;
        this.list.style.transform = '';
        this.rest();
      }
    }

    // Stato a riposo (desktop): primo capo girato, didascalia di default.
    rest() {
      this.setActive(0, true);
    }

    setActive(i, keepCaption = false) {
      this.active = i;
      this.items.forEach((item, j) => item.classList.toggle('is-turned', j === i));
      const item = keepCaption ? null : this.items[i];
      if (this.nameText) this.nameText.textContent = item ? item.dataset.title : this.defaultName;
      if (this.priceEl) this.priceEl.textContent = item ? item.dataset.price : '';
      if (this.compareEl) this.compareEl.textContent = item ? item.dataset.compare : '';
      this.arrows.forEach((btn) => {
        btn.disabled = Number(btn.dataset.dir) < 0 ? i <= 0 : i >= this.items.length - 1;
      });
    }

    // Frecce: capo precedente / successivo (su mobile lo porta anche al centro).
    step(dir) {
      const next = Math.max(0, Math.min(this.items.length - 1, this.active + dir));
      this.setActive(next);
      if (this.track) this.center();
    }

    metrics() {
      const cs = getComputedStyle(this);
      return {
        rest: parseFloat(cs.getPropertyValue('--pxs-rest')) || 40,
        turn: parseFloat(cs.getPropertyValue('--pxs-turn')) || 200,
        gap: parseFloat(cs.getPropertyValue('--pxs-gap')) || 0
      };
    }

    // Centro analitico del capo attivo: non dipende dalle transizioni in corso.
    center(animate = true) {
      if (!this.track || this.active < 0) return;
      const { rest, turn, gap } = this.metrics();
      const before = this.active * (rest + gap);
      const mid = before + turn / 2;
      this.shift = this.stage.clientWidth / 2 - mid;
      if (!animate) this.list.style.transition = 'none';
      this.list.style.transform = `translate3d(${this.shift}px, 0, 0)`;
      if (!animate) {
        this.list.offsetWidth; // eslint-disable-line no-unused-expressions
        this.list.style.transition = '';
      }
    }

    onItemClick(i, hit) {
      if (this.dragged) {
        this.dragged = false;
        return;
      }
      if (this.track && i !== this.active) {
        this.setActive(i);
        this.center();
        return;
      }
      this.open(i, hit);
    }

    onDragStart(evt) {
      this.dragged = false;
      if (!this.track || evt.pointerType === 'mouse') return;
      this.drag = { x: evt.clientX, y: evt.clientY, moving: false };
    }

    onDragMove(evt) {
      if (!this.drag) return;
      const dx = evt.clientX - this.drag.x;
      if (!this.drag.moving) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(evt.clientY - this.drag.y)) return;
        this.drag.moving = true;
        this.list.style.transition = 'none';
      }
      this.drag.dx = dx;
      this.list.style.transform = `translate3d(${this.shift + dx}px, 0, 0)`;
    }

    onDragEnd() {
      const drag = this.drag;
      this.drag = null;
      if (!drag || !drag.moving) return;
      this.dragged = true;
      this.list.style.transition = '';
      const { rest, gap } = this.metrics();
      let steps = Math.round(-drag.dx / (rest + gap));
      if (steps === 0 && Math.abs(drag.dx) > 30) steps = drag.dx < 0 ? 1 : -1;
      const next = Math.max(0, Math.min(this.items.length - 1, this.active + steps));
      this.setActive(next);
      this.center();
    }

    onDragCancel() {
      if (!this.drag) return;
      this.drag = null;
      this.list.style.transition = '';
      this.center();
    }

    /* ---------- overlay ---------- */

    initOverlay(opts) {
      this.ovStage = this.overlay.querySelector('.pxs-ov__stage');
      this.overlay.querySelector('.js-pxs-close').addEventListener('click', () => this.close(), opts);
      this.overlay.querySelector('.js-pxs-prev').addEventListener('click', (evt) => this.go(-1, evt.currentTarget), opts);
      this.overlay.querySelector('.js-pxs-next').addEventListener('click', (evt) => this.go(1, evt.currentTarget), opts);
      if (this.items.length < 2) {
        this.overlay.querySelectorAll('.pxs-ov__nav').forEach((btn) => { btn.hidden = true; });
      }

      // clic sul fondo (fuori dal contenuto) chiude
      this.overlay.addEventListener('click', (evt) => {
        if (evt.target === this.overlay || evt.target === this.ovStage) this.close();
      }, opts);

      // swipe orizzontale nel dettaglio
      this.overlay.addEventListener('pointerdown', (evt) => {
        if (evt.pointerType === 'mouse') return;
        this.swipe = { x: evt.clientX, y: evt.clientY };
      }, opts);
      this.overlay.addEventListener('pointerup', (evt) => {
        if (!this.swipe) return;
        const dx = evt.clientX - this.swipe.x;
        const dy = evt.clientY - this.swipe.y;
        this.swipe = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(dx < 0 ? 1 : -1);
      }, opts);

      this.onKeydown = (evt) => {
        if (!this.isOpen) return;
        if (evt.key === 'Escape') this.close();
        else if (evt.key === 'ArrowLeft') this.go(-1);
        else if (evt.key === 'ArrowRight') this.go(1);
      };
      document.addEventListener('keydown', this.onKeydown, opts);
    }

    open(i, opener) {
      if (!this.overlay || !this.items[i] || !this.items[i].querySelector('.pxs__tpl')) return;
      this.opener = opener || null;
      this.isOpen = true;
      document.body.appendChild(this.overlay); // sopra header e stacking context della pagina
      this.overlay.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.classList.add('is-ov-open');
      this.render(i, 0);
      this.overlay.offsetWidth; // eslint-disable-line no-unused-expressions
      this.overlay.classList.add('is-open');
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.overlay.classList.remove('is-open');
      this.classList.remove('is-ov-open');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(restoreFocus ? this.opener : null);

      if (this.track && this.ovIndex >= 0) {
        this.setActive(this.ovIndex);
        this.center(false);
      }

      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (this.isOpen) return;
        this.overlay.hidden = true;
        this.ovStage.replaceChildren();
        if (this.isConnected) this.appendChild(this.overlay);
        else this.overlay.remove();
      }, 350);
    }

    go(dir, focusEl) {
      if (!this.isOpen) return;
      const n = this.items.length;
      this.render((this.ovIndex + dir + n) % n, dir, focusEl);
    }

    render(i, dir, focusEl) {
      this.ovIndex = i;
      const tpl = this.items[i].querySelector('.pxs__tpl');
      const frag = tpl.content.cloneNode(true);
      const count = frag.querySelector('.js-pxs-count');
      if (count) count.textContent = `${pad(i + 1)} / ${pad(this.items.length)}`;

      this.ovStage.replaceChildren(frag);
      const item = this.ovStage.firstElementChild;
      if (item && dir) item.dataset.dir = dir > 0 ? 'next' : 'prev';

      this.bindFaces();
      this.bindForm();

      if (typeof trapFocus === 'function') {
        trapFocus(this.overlay, focusEl || this.overlay.querySelector('.js-pxs-close'));
      }
    }

    bindFaces() {
      const btns = Array.from(this.ovStage.querySelectorAll('.pxs-ov__face-btn'));
      const faces = Array.from(this.ovStage.querySelectorAll('.pxs-ov__face'));
      btns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const face = btn.dataset.face;
          btns.forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
          faces.forEach((f) => f.classList.toggle('is-active', f.dataset.face === face));
        });
      });
    }

    /* ---------- aggiunta al carrello ---------- */

    bindForm() {
      const form = this.ovStage.querySelector('.pxs-ov__form');
      if (!form) return;
      const json = form.querySelector('.js-pxs-variants');
      const btn = form.querySelector('.pxs-ov__atc');
      const errorEl = form.querySelector('.pxs-ov__error');
      let variants = [];
      try {
        variants = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }

      const groups = Array.from(form.querySelectorAll('.pxs-ov__opt'));
      const selected = groups.map((g) => (g.dataset.fixed !== undefined ? g.dataset.fixed : null));
      const visibleGroups = groups.filter((g) => !g.hidden);
      let current = groups.length ? null : variants[0];

      const matches = (v, skip) => selected.every((val, j) => j === skip || val === null || v.options[j] === val);

      const update = () => {
        groups.forEach((group, idx) => {
          group.querySelectorAll('.pxs-ov__pill').forEach((pill) => {
            const value = pill.dataset.value;
            const possible = variants.some((v) => v.available && v.options[idx] === value && matches(v, idx));
            pill.classList.toggle('is-unavailable', !possible);
            // con una sola opzione visibile (es. Taglia) le taglie esaurite non sono selezionabili
            if (visibleGroups.length === 1) pill.disabled = !possible;
            pill.setAttribute('aria-pressed', selected[idx] === value ? 'true' : 'false');
          });
        });

        if (groups.length) {
          current = selected.includes(null) ? null : variants.find((v) => matches(v, -1)) || null;
        }

        if (groups.length && selected.includes(null)) {
          btn.textContent = this.dataset.selectLabel;
          btn.disabled = true;
        } else if (!current || !current.available) {
          btn.textContent = this.dataset.soldoutLabel;
          btn.disabled = true;
        } else {
          btn.textContent = this.dataset.atcLabel;
          btn.disabled = false;
        }
      };

      groups.forEach((group, idx) => {
        group.addEventListener('click', (evt) => {
          const pill = evt.target.closest('.pxs-ov__pill');
          if (!pill || pill.disabled) return;
          selected[idx] = pill.dataset.value;
          errorEl.hidden = true;
          update();
        });
      });

      // un'unica variante disponibile per l'opzione visibile: preselezionala
      if (visibleGroups.length === 1) {
        const idx = groups.indexOf(visibleGroups[0]);
        const pills = Array.from(visibleGroups[0].querySelectorAll('.pxs-ov__pill'));
        if (pills.length === 1) selected[idx] = pills[0].dataset.value;
      }

      btn.addEventListener('click', () => {
        if (current && current.available) this.addToCart(current.id, btn, errorEl);
      });

      update();
    }

    async addToCart(variantId, btn, errorEl) {
      const settings = (window.theme && theme.settings) || {};
      const routes = (window.theme && theme.routes) || {};
      const cartUrl = routes.cart || '/cart';
      const cartDrawer = document.querySelector('cart-drawer');
      const useDrawer = cartDrawer && settings.afterAtc !== 'page' && settings.afterAtc !== 'no-js';

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', cartDrawer ? `${cartDrawer.dataset.section},cart-icon-bubble` : 'cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      btn.disabled = true;
      btn.classList.add('is-loading');
      errorEl.hidden = true;

      try {
        const response = await fetch(routes.cartAdd || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(typeof data.description === 'string' ? data.description : (data.message || response.status));
        }

        if (!useDrawer) {
          if (settings.afterAtc === 'nothing') {
            ParmaxStender.updateCartIcon(data);
            btn.classList.remove('is-loading');
            btn.disabled = false;
            return;
          }
          window.location.href = cartUrl;
          return;
        }

        ParmaxStender.updateCartIcon(data);
        this.close(false);
        cartDrawer.renderContents(data, true);

        fetch(`${cartUrl}.js`)
          .then((res) => res.json())
          .then((cart) => {
            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: { cart, variantId: data.variant_id }
            }));
          })
          .catch(() => {});
      } catch (error) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
        btn.disabled = false;
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    // Stessa logica di ProductForm.updateCartIcon (assets/product-form.js)
    static updateCartIcon(response) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble || !response.sections) return;
      const tmpl = document.createElement('template');
      tmpl.innerHTML = response.sections['cart-icon-bubble'] || '';
      const el = tmpl.content.querySelector('.shopify-section');
      if (el) bubble.innerHTML = el.innerHTML;
    }
  }

  customElements.define('parmax-stender', ParmaxStender);
})();
